import GlobalStoreHelper from './GlobalStoreHelper';

export default class CartActions {

    /**
     * Initialise and select the prefered delivery method by the user from previous
     * selection, or default to "delivery"
     * @return {void}
     */
    static initCartDeliverySelection() {
        GlobalStoreHelper.getCachedStock();
        CartActions.changeShippingMethod();
        CartActions.checkForCheckoutMismatch();
    }

    /**
     * Find the availability on the cart page and display info next to each product
     * @return {void}
     */
    static async fetchStockInfo() {
        // NOTE: Checkout button disable removed - button should always be active
        // Stock validation is informational only

        const body = document.getElementsByTagName('body')[0];
        if (!body || !body.classList.contains('template-cart')) return;

        const productIds = [];
        const skus = [];
        const requestedQty = [];
        const labelInfos = { delivery: null, pickup: null };

        const products = document.querySelectorAll('.cart-product-card');
        for (let i = 0; i < products.length; i++) {
            const hasWarehouseTag = !!products[i].querySelector('.cart-product-info')?.dataset?.tags.includes('warehouse-15');
            const sku = products[i].dataset.productSku;

            skus.push(sku);
            productIds.push(products[i].dataset.productId);
            requestedQty.push({
                productId: products[i].dataset.productId,
                qty: products[i].querySelector('input').value,
                hasWarehouseTag
            });
        }

        const data =  await GlobalStoreHelper.request({
            service: `locations/${GlobalStoreHelper.getItem('locationId')}/delivery-options`,
            method: 'POST',
            body: { products: requestedQty }
        })

        const locationId = GlobalStoreHelper.getItem('locationId');
        const locations = await GlobalStoreHelper.request({ service: `locations/pickups/work-times`, cache: true })
        const locationSelected = locations.find((loc) => loc.locationId == locationId);

        for (let i = 0; i < products.length; i++) {
            const productId = parseInt(products[i].dataset.productId)
            const stock = await GlobalStoreHelper.getCachedStock(productId);
            const productData = data.products.find(p => p.product_id === productId);

            // Handle case where product is not found in API response
            if (!productData || !productData.tags) {
                console.warn(`[CartActions] Product ${productId} not found in delivery-options response or missing tags`);
                continue;
            }

            const tags = productData.tags;
            const warehouseTag = !!tags.find((tag) => tag.includes('warehouse-15'));
            const peninsulaStockTag = !!tags.find((tag) => tag.includes('peninsula-stock'));
            const noEnvioTag = !!tags.find((tag) => tag === 'no_envio');
            const stockInfo = data.products.filter((stock) => stock.product_id === productId);

            const labelInfo = await GlobalStoreHelper.createPromiseObject(stock, {
                locationId,
                addStoreSelector: false,
                locSelectedIsWarehouse15: locationSelected.wareHouse15,
                warehouseTag,
                peninsulaStockTag,
                noEnvioTag
            });

            const labelInfoMethods = await GlobalStoreHelper.createPromiseObject(stock, {
                locationId,
                cartPage: true,
                newLine: false,
                addStoreSelector: false,
                locSelectedIsWarehouse15: locationSelected.wareHouse15,
                warehouseTag,
                peninsulaStockTag,
                noEnvioTag
            });

            let storeDate = new Date(labelInfoMethods.store.date);

            if(storeDate.toString() === "Invalid Date") {
                labelInfoMethods.store.date = labelInfoMethods.store.date ? new Date(labelInfoMethods.store.date.replace(/\//g,'-').split('-').reverse().join('-')) : '';
            }

            // Set the promises to highest date promise for delivery method title output
            if (!labelInfos.delivery || new Date(labelInfoMethods.online.date) > labelInfos.delivery.date) {
                labelInfos.delivery = labelInfoMethods.online;
            }
            if (!labelInfos.pickup || new Date(labelInfoMethods.store.date) > labelInfos.pickup.date) {
                labelInfos.pickup = labelInfoMethods.store;
            }

            // Go over single product in cart and determine their availability per each delivery method
            const availabilityInfos = products[i].querySelectorAll('.stock-information');
            for (let info = 0; info < availabilityInfos.length; info++) {
                const stock = availabilityInfos[info];
                const online = stock.classList.contains('online');

                // Initially check same store to satisfy express-pickup options
                const currentStoreId = GlobalStoreHelper.getItem('locationId');
                let locationInfo = stockInfo.find((loc) => loc.onlineStore === online && loc.location_id === currentStoreId);

                // Currently selected store is not activated for deliveries, select next store that perform deliveries
                if (!locationInfo) {
                    locationInfo = stockInfo.find((loc) => loc.onlineStore === online);
                }

                const bubbleDom = stock.querySelector('.bubble');
                const stockMessageDom = stock.querySelector('.stock-message');
                if (bubbleDom.classList.length > 0) {
                    bubbleDom.classList.remove('red', 'yellow', 'green');
                }
                // Hide delivery option if message is not provided
                if ((online && !labelInfo.online.message) || (!online && !labelInfo.store.message)) {
                    stock.classList.add('d-none');
                }
                bubbleDom.classList.add(online ? labelInfo.online.color : labelInfo.store.color);
                stockMessageDom.innerHTML = online ? labelInfo.online.message : labelInfo.store.message;
            }
        }

        // Set Orders Tags
        if(data.ordersTags) document.querySelector('input[name="attributes[Tags-To-Order]"]').value = data.ordersTags;

        // Check the products tags
        let flag = false;

        for (let i = 0; i < data.products.length; i++) {
          if (flag) break;
          flag = !!data.products[i].tags.find((tag) => {
            tag = tag.trim();

            return (
              tag === "presale-withoutdate" ||
              tag === "presale-noprevision" ||
              tag === "reserve-noprevision" ||
              tag.includes("predelivery-") ||
              tag.includes("presale-")
            );
          });
        }

        const productsUniques = data.products.reduce((prev, current) => {
            const exists = !!prev.find((prod) => prod.product_id === current.product_id);

            if(!exists)
                prev.push(current);

            return prev
        }, []);

        CartActions.togglePreSaleOrderWarning(flag ? flag && productsUniques.length > 1 : data.hasPartialPresale && productsUniques.length > 1);

        // Always enable checkout button - stock validation removed
        CartActions.recheckContinueButton(true);
        CartActions.setCurrentStore()

        // Check and update quantity limit buttons after stock info is fetched
        if (window.quantityLimits && window.quantityLimits.updateQuantityButtons) {
            window.quantityLimits.updateQuantityButtons();
        }
    }

    /**
     * Find out stock availability across all locations to populate store selector on the cart page
     * @returns {Promise<void>}
     */
    static async fetchMultipleProductLocations() {
        const productIds = [];
        const products = document.querySelectorAll('.cart-product-card');

        for (let i = 0; i < products.length; i++) {
            const inputQty = products[i].querySelector('div.quantity-change input');
            productIds.push({productId: products[i].dataset.productId, requestedQty: inputQty && Number(inputQty.value|| 1)});
        }

        const data = await GlobalStoreHelper.request({ service: `products/stock-at-locations`, method: 'POST', body: {
            productsIds: productIds.map((product) => product.productId),
        }})

        let dataWithRequestedQty = {};

        Object.entries(data).forEach(([key, value]) =>
            dataWithRequestedQty[key] = value.map((v) => ({...v, requestedQty: productIds.find((product) => product.productId === v.productId)?.requestedQty}))
        );

        return dataWithRequestedQty;
    }

    /**
     * Performs HTML changes to indicate the currently active shipping method
     * @param  {string} methodType identificator of shipping method
     * @param  {boolean} init whether selection happens on existing choice on cart load
     * @return {void}
     */
    static changeShippingMethod() {
        if(GlobalStoreHelper.ifStoreSelected()) CartActions.fetchStockInfo();
    }

    /**
     * Perform check on "continue"  button in cart and toggle it accordingly
     * NOTE: Modified to always enable checkout button - stock validation is informational only
     */
    static recheckContinueButton(_active) {
        const continueBtn = document.querySelector('#update-cart-submit');
        const shopPayButton = document.querySelector("#button-shop-pay");

        // Always enable checkout button
        if (continueBtn) {
            continueBtn.classList.remove('disabled');
            continueBtn.disabled = false;
        }

        if (shopPayButton) {
            shopPayButton.classList.remove('disabled');
            shopPayButton.disabled = false;
        }
    }

    /**
     * Check whether localstorage has indication from the checkout flow
     * that cart contents must be reviewed. If it does, then show the message
     * and remove the flag from localStorage.
     */
    static checkForCheckoutMismatch() {
        const settings = GlobalStoreHelper.settings();
        const showMessage = GlobalStoreHelper.getItem('checkoutMismatch')
        if (showMessage) {
            const alert = document.querySelector('.checkout-cart-error');
            alert.classList.remove('hidden');
            localStorage.removeItem(settings.checkoutMismatch);
        }
    }

    /**
     * Show or hide presale warning message on top of cart page when there is a combination of
     * pre-sale item and regular items in the same cart.
     * @param showWarning boolean whether to show a warning
     */
    static togglePreSaleOrderWarning(showWarning) {
        console.log({showWarning})
        const alert = document.querySelector('.checkout-cart-presale-warning');
        if (showWarning) {
            alert.classList.remove('hidden');
        } else {
            alert.classList.add('hidden');
        }
    }

    static async removeShopPayNote() {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout

            await fetch("/cart/update.js", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  note: ''
                }),
                signal: controller.signal
            });

            clearTimeout(timeoutId);
        } catch (error) {
            if (error.name === 'AbortError') {
                console.warn('Shop pay note removal timed out - continuing checkout');
            } else {
                console.warn('Failed to remove shop pay note:', error);
            }
            // Continue silently - this is not critical for checkout
        }
    }

    static setCurrentStore(){
        const storeLocationElm = document.querySelector('.cart-shipping-information .store-location');
        const locationNameElm = document.getElementById('location-name');
        const locations = GlobalStoreHelper.getItem('locations')
        const locationId = GlobalStoreHelper.getItem('locationId')

        locationNameElm && (locationNameElm.innerHTML = locations ? locations[locationId]?.name || '' : '');
        storeLocationElm && (storeLocationElm.classList.remove('d-none'));
    }
}